import {useCallback} from 'react';

import {ContactField} from '@target-web/leadads-form-view';
import {useLeadFormAnalyticsMutation} from 'api/services/leads';
import {EventEnum, LeadBlockQuestion} from 'api/types/Analytics';
import {useSearchBaseParams} from 'utils/searchParamsContext';
import {LoadResourceError, LoadResourceMetrics} from 'utils/types';

function useAnalytics() {
    const {isLeadFormType} = useSearchBaseParams();
    const [, mutateAnalytics] = useLeadFormAnalyticsMutation();

    const hideFormAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: isLeadFormType ? EventEnum.HideForm : EventEnum.HideSurvey}));
    }, [mutateAnalytics, isLeadFormType]);

    const restoreFormAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: isLeadFormType ? EventEnum.RestoreForm : EventEnum.RestoreSurvey}));
    }, [mutateAnalytics, isLeadFormType]);

    const clickToCallAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: EventEnum.ClickToCall}));
    }, [mutateAnalytics]);

    const clickToExternalLinkAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: EventEnum.ClickToExternalLink}));
    }, [mutateAnalytics]);

    const onAppReadyAnalyticEvent = useCallback(() => {
        mutateAnalytics(
            JSON.stringify({
                event: EventEnum.app_ready,
                data: {
                    time_in_ms: performance.now(),
                    // #TODO
                    // release: ,
                    // user_id: ,
                },
            }),
        );
    }, [mutateAnalytics]);

    const onResourceLoadedAnalyticEvent = useCallback(
        (data: LoadResourceMetrics) => {
            if (data.transfer_size === 0) {
                // если файл из кэша (data.size > 0), то не отправляем
                // если ошибка по отсутствию Timing-Allow-Origin (data.size === 0), не отправляем
                return;
            }
            mutateAnalytics(JSON.stringify({event: EventEnum.resource_loaded, data}));
        },
        [mutateAnalytics],
    );

    const onResourceLoadingErrorAnalyticEvent = useCallback(
        (data: LoadResourceError) => {
            mutateAnalytics(JSON.stringify({event: EventEnum.resource_loading_error, data}));
        },
        [mutateAnalytics],
    );

    const onAppResourcesLoadedAnalyticEvent = useCallback(() => {
        mutateAnalytics(
            JSON.stringify({
                event: EventEnum.app_resources_loaded,
                data: {
                    time_in_ms: performance.now(),
                    // #TODO
                    // release: ,
                    // user_id: ,
                },
            }),
        );
    }, [mutateAnalytics]);

    const closeFormAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: isLeadFormType ? EventEnum.CloseForm : EventEnum.CloseSurvey}));
    }, [mutateAnalytics, isLeadFormType]);

    const closeCaptchaAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: EventEnum.closeCaptcha}));
    }, [mutateAnalytics]);

    const openPageAnalyticEvent = useCallback(
        (page: string, from: string = '', isBack: boolean = false) => {
            mutateAnalytics(
                JSON.stringify({
                    event: EventEnum.OpenPage,
                    data: {page, from, is_back: isBack},
                }),
            );
        },
        [mutateAnalytics],
    );

    const openFormAnalyticEvent = useCallback(() => {
        mutateAnalytics(
            JSON.stringify({
                event: isLeadFormType ? EventEnum.OpenForm : EventEnum.OpenSurvey,
                data: {
                    width: document.documentElement.clientWidth,
                    height: document.documentElement.clientHeight,
                },
            }),
        );
    }, [mutateAnalytics, isLeadFormType]);

    const leadAnswerAnalyticEvent = useCallback(
        (data: LeadBlockQuestion) => {
            mutateAnalytics(
                JSON.stringify({event: isLeadFormType ? EventEnum.lead_answer : EventEnum.respondent_answer, data}),
            );
        },
        [mutateAnalytics, isLeadFormType],
    );

    const contactInfoFillEvent = useCallback(
        (field: ContactField) => {
            mutateAnalytics(JSON.stringify({event: EventEnum.contact_info_fill, data: {field}}));
        },
        [mutateAnalytics],
    );

    const getLeadFormDataAnalyticEvent = useCallback(
        (time_in_ms: number, is_first = false) => {
            mutateAnalytics(
                JSON.stringify({
                    event: isLeadFormType ? EventEnum.get_leadform_data : EventEnum.get_survey_form_data,
                    data: {
                        time_in_ms,
                        is_first,
                    },
                }),
            );
        },
        [mutateAnalytics, isLeadFormType],
    );

    const phoneBannedAnalyticEvent = useCallback(() => {
        mutateAnalytics(JSON.stringify({event: EventEnum.PhoneBanned}));
    }, [mutateAnalytics]);

    return {
        openPageAnalyticEvent,
        openFormAnalyticEvent,
        onAppReadyAnalyticEvent,
        onAppResourcesLoadedAnalyticEvent,
        clickToCallAnalyticEvent,
        clickToExternalLinkAnalyticEvent,
        mutateAnalytics,
        hideFormAnalyticEvent,
        closeFormAnalyticEvent,
        closeCaptchaAnalyticEvent,
        restoreFormAnalyticEvent,
        leadAnswerAnalyticEvent,
        getLeadFormDataAnalyticEvent,
        contactInfoFillEvent,
        phoneBannedAnalyticEvent,
        onResourceLoadedAnalyticEvent,
        onResourceLoadingErrorAnalyticEvent,
    };
}

export default useAnalytics;
